import React, { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { 
  Users, 
  Plus, 
  Search, 
  Edit, 
  Trash2, 

  RefreshCw,

} from 'lucide-react';
import api from '../services/api';
import { toast } from 'react-hot-toast';
import { CourtTypeCreateModal, CourtTypeEditModal, CourtTypeReplaceModal, CourtTypeViewModal } from '../components/CourtModal';
import { useAuth } from '../contexts/AuthContext';

// User interface
interface CourtType {
  id: number;
  name: string;
  created_at: string;
  updated_at: string;
}

interface CourtTypesResponse {
  courtTypes: CourtType[];
  pagination: {
    page: number;
    per_page: number;
    total: number;
    pages: number;
    has_next: boolean;
    has_prev: boolean;
  };
}

const CourtTypeManagement: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedUser, setSelectedUser] = useState<CourtType | null>(null);
  const { onProfileUpdate } = useAuth();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isReplaceModalOpen, setIsReplaceModalOpen] = useState(false);
  const [courtTypes, setCourtTypes] = useState<string[]>([]);
  const queryClient = useQueryClient();

  // Listen for profile updates and invalidate court type queries if needed
  useEffect(() => {
    const unsubscribe = onProfileUpdate(() => {
      // Invalidate court type queries when profile is updated
      queryClient.invalidateQueries(['court-types']);
    });
    
    return unsubscribe;
  }, [onProfileUpdate, queryClient]);

  const {
    data: courtTypesData,
    isLoading,
    error
  } = useQuery<CourtTypesResponse>(
    ['court-types', currentPage, searchTerm],
    async () => {
      const params = new URLSearchParams({
        page: currentPage.toString(),
        per_page: '10'
      });
      
      if (searchTerm) {
        params.append('search', searchTerm.trim());
      }

      const response = await api.get(`/api/court-types?${params}`);
      console.log(response.data);
      return response.data;
    },
    {
      keepPreviousData: true,
      staleTime: 5 * 60 * 1000, // 5 minutes
    }
  );

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
  };

  const handleCreateUser = () => {
    setSelectedUser(null);
    setIsCreateModalOpen(true);
  };

  const handleEditUser = (courtType: CourtType) => {
    setSelectedUser(courtType);
    setIsEditModalOpen(true);
  };


  const handleDeleteUser = async (courtType: CourtType) => {

    // Check if the court type is used in any documents
    const name = courtType.name;
    //console.log(name);
    const data = await api.get(`/api/documents`, {
      params: { 
        court_type: name
      }
    });

    //console.log(data.data);

    if (data.data?.documents?.length > 0) {
      toast.error('Court type is used in Documents');
      // Open replace modal
      setIsReplaceModalOpen(true);
      setSelectedUser(courtType);
      return;
    }
    else{
      
      if (!window.confirm('Are you sure you want to delete this Court Type? This action cannot be undone.')) {
        return;
      }

      try {
        await api.delete(`/api/court-types/${courtType.id}`);
        toast.success('Court Type deleted successfully');
        queryClient.invalidateQueries(['court-types']);
      } catch (error: any) {
        toast.error(error.response?.data?.error || 'Failed to delete Court Type');
      }
    }
    

    
  };


  if (error) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <Users className="w-16 h-16 text-red-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Error Loading Court Types
          </h3>
          <p className="text-gray-500 dark:text-gray-400">
            Failed to load Court Type data. Please try again.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            Courts
          </h1>
          <button
            onClick={handleCreateUser}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            <Plus className="w-5 h-5" />
            <span>Add Court</span>
          </button>
        </div>

        {/* Search */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
          <div className="p-4">
            <form onSubmit={handleSearch} className="flex space-x-4">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search court types by name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>
              <button
                type="submit"
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Search
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Court Types Table */}
      {isLoading ? (
        <div className="text-center py-12">
          <RefreshCw className="w-8 h-8 text-blue-600 mx-auto mb-4 animate-spin" />
          <p className="text-gray-500 dark:text-gray-400">Loading court types...</p>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Court
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Updated
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {courtTypesData?.courtTypes?.map((courtType) => (
                  <tr
                    key={courtType.id}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center">
                            <span className="text-white font-medium">
                              {courtType.name ? courtType.name[0].toUpperCase() : 'N/A'}
                            </span>
                          </div>
                        </div>
                        <div>
                        <div className="ml-4">
                          <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                            {courtType.name ? `${courtType.name}` : 'N/A'}
                          </h3>
                        </div>
                        {/* <p className="text-gray-500 dark:text-gray-400">{user.email}</p>
                        <div className="flex items-center space-x-2 mt-2">
                          {getRoleBadge(user.role)}
                          {getStatusBadge(user.is_active, user.is_email_verified)}
                        </div> */}
                      </div>
                      </div>
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {new Date(courtType.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {new Date(courtType.updated_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        
                        <button
                          onClick={() => handleEditUser(courtType)}
                          className="text-gray-600 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400"
                          title="Edit Court Type"
                        >
                          <Edit className="w-4 h-4" />
                        </button>

                        <button
                          onClick={() => handleDeleteUser(courtType)}
                          className="text-red-600 hover:text-red-900 dark:text-red-400"
                            title="Delete Court Type"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {courtTypesData?.pagination && courtTypesData.pagination.pages > 1 && (
            <div className="bg-white dark:bg-gray-800 px-4 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-700 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={!courtTypesData?.pagination?.has_prev}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={!courtTypesData?.pagination?.has_next}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    Showing{' '}
                    <span className="font-medium">
                      {(currentPage - 1) * courtTypesData?.pagination?.per_page + 1}
                    </span>{' '}
                    to{' '}
                    <span className="font-medium">
                        {Math.min(currentPage * courtTypesData?.pagination?.per_page, courtTypesData?.pagination?.total)}
                    </span>{' '}
                    of{' '}
                    <span className="font-medium">{courtTypesData?.pagination?.total}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={!courtTypesData?.pagination?.has_prev}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Previous
                    </button>
                    {Array.from({ length: courtTypesData?.pagination?.pages }, (_, i) => i + 1)
                      .filter(page => 
                        page === 1 || 
                        page === courtTypesData?.pagination?.pages || 
                        Math.abs(page - currentPage) <= 2
                      )
                      .map((page, index, array) => (
                        <React.Fragment key={page}>
                          {index > 0 && array[index - 1] !== page - 1 && (
                            <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-700 dark:text-gray-300">
                              ...
                            </span>
                          )}
                          <button
                            onClick={() => setCurrentPage(page)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              page === currentPage
                                ? 'z-10 bg-blue-50 dark:bg-blue-900 border-blue-500 text-blue-600 dark:text-blue-200'
                                : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                            }`}
                          >
                            {page}
                          </button>
                        </React.Fragment>
                      ))
                    }
                    <button
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={!courtTypesData?.pagination?.has_next}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-sm font-medium text-gray-500 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Next
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Modals */}
      <CourtTypeCreateModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={() => queryClient.invalidateQueries(['court-types'])}
      />
      
      <CourtTypeEditModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSuccess={() => queryClient.invalidateQueries(['court-types'])}
        courtType={selectedUser}
      />
      
      <CourtTypeViewModal
        isOpen={isViewModalOpen}
        onClose={() => setIsViewModalOpen(false)}
        courtType={selectedUser}
      />

      <CourtTypeReplaceModal
        isOpen={isReplaceModalOpen}
        onClose={() => setIsReplaceModalOpen(false)}
        onSuccess={() => queryClient.invalidateQueries(['court-types'])}
        courtType={selectedUser}
      />
      
      
    </div>
  );
};

export default CourtTypeManagement;
